import React from 'react';

import CalculatorBase from './calculator-base';
import CalculatorInput from './calculator-input';
import CalculatorOutput from './calculator-output';
import { PieChart } from '../chart';

import { calculateAssetOutputs } from '../../utilities/calculations';

const renderBasicInputs = (state, processInput) => {
    const { costOfAsset, downPayment, interestRate, loanPeriod } = state;

    return (
        <div>
            <CalculatorInput
                id="costOfAsset"
                label="Price"
                defaultValue={costOfAsset}
                processInput={processInput}
                maxSliderValue={100000}
            />
            <CalculatorInput
                id="downPayment"
                label="Down Payment"
                defaultValue={downPayment}
                processInput={processInput}
                maxSliderValue={costOfAsset}
            />
            <CalculatorInput
                id="interestRate"
                label="Interest Rate"
                defaultValue={interestRate}
                format="percent"
                processInput={processInput}
            />
            <CalculatorInput
                id="loanPeriod"
                label="Loan Period in Years"
                defaultValue={loanPeriod}
                format="int"
                processInput={processInput}
                maxSliderValue={50}
            />
        </div>
    );
};

const renderBasicOutputs = (state) => {
    const { monthlyPaymentBasic } = calculateAssetOutputs(state);
    const { loanPeriod } = state;
    const loanPaymentLifetime = loanPeriod * 12 * monthlyPaymentBasic;

    return [
        <CalculatorOutput label="Payments & Expenses (monthly)" headingLevel={1} />,
        <CalculatorOutput label="Loan Payment" value={monthlyPaymentBasic} indentLevel={1} />,
        <CalculatorOutput label="Total Monthly Expenses" value={monthlyPaymentBasic} headingLevel={1} />,
        <br />,
        <CalculatorOutput label="Lifetime Totals" headingLevel={1} />,
        <CalculatorOutput label="Loan Payments" value={loanPaymentLifetime} indentLevel={1} />,
        <CalculatorOutput label="Total Payments" value={loanPaymentLifetime} headingLevel={1} />,
    ];
};

const renderBasicChart = (state) => {
    const { monthlyPaymentBasic } = calculateAssetOutputs(state);
    const data = [{ x: 'Loan Payment', y: monthlyPaymentBasic }];

    return <div css={{ width: '50%' }}><PieChart data={data} labelText="Monthly Expenses" /></div>;
};

const renderAdvancedInputs = (state, processInput) => {
    const { salesTax, insurance, usageCost, ownershipPeriod, feesYearly, zipCode, maintenenceYearly } = state;

    return (
        <div>
            {renderBasicInputs(state, processInput)}
            <CalculatorInput
                label="Zip Code"
                id="zipCode"
                defaultValue={zipCode}
                format="int"
                controlledId="salesTax"
                processInput={processInput}
                showSlider={false}
                level={1}
            />
            <CalculatorInput
                label="Sales Tax"
                id="salesTax"
                defaultValue={salesTax}
                format="percent"
                processInput={processInput}
                level={1}
            />
            <CalculatorInput
                label="Insurance Monthly"
                id="insurance"
                defaultValue={insurance}
                processInput={processInput}
                level={1}
            />
            <CalculatorInput
                label="Registration Yearly"
                id="feesYearly"
                defaultValue={feesYearly}
                processInput={processInput}
                level={1}
            />
            <CalculatorInput
                label="How long will you own car?"
                id="ownershipPeriod"
                defaultValue={ownershipPeriod}
                format="int"
                processInput={processInput}
                maxSliderValue={50}
                level={1}
            />
            <CalculatorInput
                label="Gas"
                id="usageCost"
                defaultValue={usageCost}
                processInput={processInput}
                level={1}
            />
            <CalculatorInput
                label="Yearly Maintenence"
                id="maintenenceYearly"
                defaultValue={maintenenceYearly}
                processInput={processInput}
                level={1}
            />
        </div>
    );
};

const renderAdvancedOutputs = (state) => {
    const { monthlyPaymentAdvanced, monthlyCostOfOperation, paymentAndOperationsCost } = calculateAssetOutputs(state);
    const { loanPeriod, ownershipPeriod } = state;
    const loanPaymentLifetimeAdvanced = loanPeriod * 12 * monthlyPaymentAdvanced;
    const totalMonthlyAdvanced = monthlyPaymentAdvanced + monthlyCostOfOperation;
    const lifetimeCostOfOperation = monthlyCostOfOperation * ownershipPeriod * 12;

    return [
        <CalculatorOutput label="Payments & Expenses (monthly)" headingLevel={1} />,
        <CalculatorOutput label="Loan Payment" value={monthlyPaymentAdvanced} indentLevel={1} />,
        <CalculatorOutput label="Operational Costs" value={monthlyCostOfOperation} indentLevel={1} level={1} />,
        <CalculatorOutput label="Total Monthly Expenses" value={totalMonthlyAdvanced} headingLevel={1} level={1} />,
        <br />,
        <CalculatorOutput label="Lifetime Totals" headingLevel={1} />,
        <CalculatorOutput label="Loan Payments" value={loanPaymentLifetimeAdvanced} indentLevel={1} level={1} />,
        <CalculatorOutput label="Operational Costs" value={lifetimeCostOfOperation} indentLevel={1} level={1} />,
        <CalculatorOutput label="Total Payments" value={paymentAndOperationsCost} headingLevel={1} level={1} />,
    ];
};

const renderAdvancedChart = (state) => {
    const { monthlyPaymentAdvanced, monthlyCostOfOperation } = calculateAssetOutputs(state);
    const data = [{ x: 'Loan Payment', y: monthlyPaymentAdvanced }, { x: 'Operational Costs', y: monthlyCostOfOperation }];

    return <div css={{ width: '50%' }}><PieChart data={data} labelText="Monthly Expenses" /></div>;
};


const renderWyseInputs = (state, processInput) => {
    const { depreciationRate } = state;

    return (
        <div>
            {renderAdvancedInputs(state, processInput)}
            <CalculatorInput
                label="Depreciation Rate"
                id="depreciationRate"
                defaultValue={depreciationRate}
                processInput={processInput}
                format="percent"
                level={2}
            />
        </div>
    );
};

const renderWyseOutputs = (state) => {
    const { opportunityCost, depreciationCost, valueEOL, totalCost } = calculateAssetOutputs(state);

    return [
        renderAdvancedOutputs(state),
        <br />,
        <CalculatorOutput label="Opportunity Cost" value={opportunityCost} key="opportunityCost" level={2} />,
        <CalculatorOutput label="Depreciation" value={depreciationCost} key="depreciationCost" level={2} />,
        <CalculatorOutput label="End-of-life Value" value={valueEOL} key="valueEOL" level={2} />,
        <br />,
        <CalculatorOutput label="Total Cost of Ownership" value={totalCost} key="totalCost" headingLevel={1} level={2} />,
    ];
};

const renderWyseChart = renderAdvancedChart;

export default props => (
    <CalculatorBase
        basicInputs={renderBasicInputs}
        basicOutputs={renderBasicOutputs}
        advancedInputs={renderAdvancedInputs}
        advancedOutputs={renderAdvancedOutputs}
        wyseInputs={renderWyseInputs}
        wyseOutputs={renderWyseOutputs}
        {...props}
    />
);
